import {type ReactNode} from 'react'
import {Box, Typography} from '@mui/material'
import {Link as RouterLink} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'

// Compact upsell panel shown next to the progress bar in the free-mode
// export dialog. Reuses the freeInfoColumn.* i18n keys defined for the
// fullscreen free-editor right column.

interface Feat {
  key: string
  icon: ReactNode
}

const iconStrokeProps = {
  width: 16,
  height: 16,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
}

const FEATS: Feat[] = [
  {
    key: 'save',
    icon: (
      <svg {...iconStrokeProps}>
        <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
        <path d="M17 21v-8H7v8M7 3v5h8" />
      </svg>
    ),
  },
  {
    key: 'scenes',
    icon: (
      <svg {...iconStrokeProps}>
        <path d="M12 2l9 5-9 5-9-5 9-5z" />
        <path d="M3 12l9 5 9-5" />
        <path d="M3 17l9 5 9-5" />
      </svg>
    ),
  },
  {
    key: 'device',
    icon: (
      <svg {...iconStrokeProps}>
        <rect x="6" y="2" width="12" height="20" rx="2.5" />
        <path d="M11 18h2" />
      </svg>
    ),
  },
  {
    key: 'fork',
    icon: (
      <svg {...iconStrokeProps}>
        <circle cx="6" cy="5" r="2" />
        <circle cx="18" cy="5" r="2" />
        <circle cx="12" cy="19" r="2" />
        <path d="M6 7v3a3 3 0 003 3h6a3 3 0 003-3V7M12 13v4" />
      </svg>
    ),
  },
  {
    key: 'noSub',
    icon: (
      <svg {...iconStrokeProps}>
        <path d="M20.6 13.4l-7.2 7.2a2 2 0 01-2.8 0L3 12.8V4h8.8l8.8 8.8a2 2 0 010 2.6z" />
        <circle cx="8.5" cy="8.5" r="1.3" />
      </svg>
    ),
  },
]

const arrowIcon = (
  <svg
    width={13}
    height={13}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M5 12h14" />
    <path d="M13 5l7 7-7 7" />
  </svg>
)

const FreeExportAdPanel = () => {
  const {t} = useTranslation()
  const tokens = useAppTokens()
  const sx = buildStyles(tokens)

  return (
    <Box sx={sx.root}>
      <Box sx={sx.header}>
        <Typography sx={sx.eyebrow}>{t('freeInfoColumn.eyebrow')}</Typography>
        <Typography sx={sx.heading}>{t('freeInfoColumn.heading')}</Typography>
      </Box>

      <Box component="ul" sx={sx.featList}>
        {FEATS.map(({key, icon}) => (
          <Box component="li" key={key} sx={sx.feat}>
            <Box sx={sx.featIcon} aria-hidden>
              {icon}
            </Box>
            <Box sx={sx.featBody}>
              <Typography sx={sx.featLabel}>{t(`freeInfoColumn.feats.${key}.label`)}</Typography>
              <Typography sx={sx.featDesc}>{t(`freeInfoColumn.feats.${key}.desc`)}</Typography>
            </Box>
          </Box>
        ))}
      </Box>

      <Box component={RouterLink} to="/login" sx={sx.cta}>
        <Box sx={sx.ctaMain}>
          {t('freeInfoColumn.cta')}
          {arrowIcon}
        </Box>
        <Typography sx={sx.ctaHint}>{t('freeInfoColumn.ctaHint')}</Typography>
      </Box>
    </Box>
  )
}

const buildStyles = (tokens: ThemeTokens) => ({
  root: {
    width: 320,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
    padding: '18px',
    borderRadius: '12px',
    border: `1px solid ${tokens.accordion.border}`,
    background: 'rgba(255, 255, 255, 0.02)',
  },
  header: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
  },
  eyebrow: {
    fontSize: 10.5,
    fontWeight: 600,
    letterSpacing: '0.14em',
    textTransform: 'uppercase' as const,
    color: tokens.accent.light,
  },
  heading: {
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1.25,
    letterSpacing: '-0.01em',
    color: tokens.text.primary,
  },
  featList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '10px',
  },
  feat: {
    display: 'flex',
    gap: '10px',
    alignItems: 'flex-start',
  },
  featIcon: {
    flexShrink: 0,
    width: 26,
    height: 26,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '7px',
    background: 'linear-gradient(180deg, rgba(124, 92, 255, 0.18), rgba(124, 92, 255, 0.08))',
    color: tokens.accent.light,
  },
  featBody: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
    minWidth: 0,
  },
  featLabel: {
    fontSize: 12.5,
    fontWeight: 600,
    color: tokens.text.primary,
    lineHeight: 1.25,
  },
  featDesc: {
    fontSize: 11.5,
    color: tokens.text.secondary,
    lineHeight: 1.35,
  },
  cta: {
    display: 'inline-flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: '2px',
    minHeight: 50,
    padding: '6px 16px',
    borderRadius: '10px',
    border: '1px solid transparent',
    background: 'linear-gradient(180deg, #8a6dff, #6446f5)',
    color: '#ffffff',
    textDecoration: 'none',
    boxShadow: '0 10px 28px -8px rgba(124, 92, 255, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
    transition: 'background 0.18s, transform 0.15s',
    '&:hover': {
      background: 'linear-gradient(180deg, #9379ff, #7053ff)',
      transform: 'translateY(-1px)',
    },
  },
  ctaMain: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: 13.5,
    fontWeight: 600,
    lineHeight: 1.1,
  },
  ctaHint: {
    fontSize: 10.5,
    fontWeight: 500,
    lineHeight: 1.1,
    color: 'rgba(255, 255, 255, 0.75)',
    letterSpacing: '0.01em',
  },
})

export default FreeExportAdPanel
